import React, { useState } from "react";
import Skeleton from "react-loading-skeleton";
import Swal from "sweetalert2";
import Badge from "../../../../library/Badge";
import DataTable, { DataTableMultiSelectFilter, DataTableStatusFilter } from "../../../../library/DataTable";
import IconButton, { StyledActionGroup } from "../../../../library/IconButton";
import Modal from "../../../../library/Modal";
import { AddModuleToProgramme } from "../../../../../services/ProgramService";
import { useVersionModulesList } from "../../../../../query/hooks/QueryHooksLinkedProgrammes";
import { resolveNetsuiteProductName } from "../../../../../utils/VersionModulesUtils";
import { getRowsPerPageOptions } from "../../studyPaths/studyPaths.utils";
import useVersionModuleRows from "../hooks/useVersionModuleRows";
import { getVersionModulesExportOptions } from "./VersionModulesExport";
import { PortalNameCell, PreRequisitesCell } from "./VersionModulesRowCells";

/**
 * Assign Module modal: search/filter the modules not yet on this version and assign one - same
 * Modal/DataTable shell and cells as the Version Modules table and the Assign Modules modal.
 * @param {string | number} programmeId PMC programme id.
 * @param {string | number} versionId Version the modules are assigned to.
 * @param {Array} netsuiteList NetSuite items, for the NetSuite Product column.
 * @param {boolean} isNetsuiteLoading Whether `netsuiteList` is still loading.
 * @param {Map<number, string>} moduleNameById System-wide module id -> Portal name lookup.
 * @param {() => Promise} onAssigned Refreshes the version's own modules list after an assign.
 * @param {() => void} onClose Closes the modal.
 */
function AssignVersionModuleModal({ programmeId, versionId, netsuiteList, isNetsuiteLoading, moduleNameById, onAssigned, onClose }) {
    const [assigningId, setAssigningId] = useState(null);
    const { data: moduleRows, isLoading, isError, refetch } = useVersionModulesList(programmeId, versionId, "available");
    const tableState = useVersionModuleRows(moduleRows);

    const handleAssign = async (module) => {
        const confirmation = await Swal.fire({
            title: "Are you sure?",
            icon: "confirm",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, Add it!",
        });

        if (!confirmation.isConfirmed) {
            return;
        }

        setAssigningId(module.Id);

        try {
            const response = await AddModuleToProgramme({ PId: programmeId, MId: module.Id, version_id: versionId });
            await Promise.all([refetch(), onAssigned()]);
            Swal.fire("Added!", `${response?.data}`, "success");
        } catch (error) {
            console.log("AssignVersionModuleModal.js->handleAssign->Error", error);
            Swal.fire("Module couldn't be added", error?.response?.data?.message || "Please try again.", "error");
        } finally {
            setAssigningId(null);
        }
    };

    const columns = [
        {
            key: "Portal",
            label: "Portal Name",
            render: (module) => <PortalNameCell row={module} />,
        },
        {
            key: "Code",
            label: "Code",
            render: (module) => module.Code || "-",
        },
        {
            key: "credits",
            label: "Cr",
            render: (module) => (module.credits ? `${module.credits}cr` : "-"),
        },
        {
            key: "term",
            label: "Term",
            render: (module) => module.term ?? "-",
        },
        {
            key: "year",
            label: "Year",
            render: (module) => module.year ?? "-",
        },
        {
            key: "Programmes",
            label: "",
            icon: "books",
            ariaLabel: "Linked programmes",
            render: (module) => (
                module.Programmes ? module.Programmes : <Badge tone="warning" icon="exclamation-triangle">0</Badge>
            ),
        },
        {
            key: "netsuite_product",
            label: "NetSuite Product",
            sortable: false,
            render: (module) => (
                isNetsuiteLoading ? <Skeleton width="70%" /> : resolveNetsuiteProductName(netsuiteList, module.netsuite_product)
            ),
        },
        {
            key: "prerequisites",
            label: "Pre-Requisites",
            sortable: false,
            render: (module) => <PreRequisitesCell row={module} moduleNameById={moduleNameById} />,
        },
        {
            key: "action",
            label: "Action",
            sortable: false,
            width: "1%",
            render: (module) => (
                <StyledActionGroup>
                    <IconButton
                        icon="plus"
                        variant="solid"
                        ariaLabel={`Assign ${module.Portal}`}
                        title={`Assign ${module.Portal}`}
                        disabled={assigningId !== null}
                        disabledReason="A module is currently being assigned"
                        onClick={() => handleAssign(module)}
                    />
                </StyledActionGroup>
            ),
        },
    ];

    return (
        <Modal title="Assign Module" onClose={onClose} width={1100} headerTone="brand">
            <DataTable
                columns={columns}
                data={isError ? [] : tableState.rows}
                keyField="Id"
                loading={isLoading}
                emptyMessage={isError ? "Modules couldn't be loaded." : "No modules match your search and filters."}
                sortKey={tableState.sortKey}
                sortOrder={tableState.sortOrder}
                onSortChange={tableState.changeSort}
                searchValue={tableState.search}
                onSearchChange={tableState.setSearch}
                onReset={tableState.resetFilters}
                exportOptions={getVersionModulesExportOptions(
                    { ...tableState, loading: isLoading, isError, netsuiteList, isNetsuiteLoading, moduleNameById },
                    "Available_Modules"
                )}
                filterSlot={(
                    <>
                        <DataTableStatusFilter
                            value={tableState.statusFilter}
                            onChange={(event) => tableState.setStatusFilter(event.target.value)}
                            options={tableState.statusOptions}
                        />
                        <DataTableMultiSelectFilter
                            placeholder="Term"
                            value={tableState.termFilter}
                            onChange={(event) => tableState.setTermFilter(event.target.value)}
                            options={tableState.termOptions}
                        />
                        <DataTableMultiSelectFilter
                            placeholder="Year"
                            value={tableState.yearFilter}
                            onChange={(event) => tableState.setYearFilter(event.target.value)}
                            options={tableState.yearOptions}
                        />
                    </>
                )}
                pagination={{
                    totalEntries: tableState.totalRows,
                    rangeStart: tableState.rangeStart,
                    rangeEnd: tableState.rangeEnd,
                    rowsPerPage: tableState.perPage,
                    rowsPerPageOptions: getRowsPerPageOptions(tableState.totalRows),
                    onRowsPerPageChange: (value) => {
                        tableState.setPerPage(value);
                        tableState.setPage(1);
                    },
                    currentPage: tableState.page,
                    totalPages: tableState.totalPages,
                    onPageChange: tableState.setPage,
                }}
            />
        </Modal>
    );
}

AssignVersionModuleModal.displayName = "AssignVersionModuleModal";

export default AssignVersionModuleModal;
